<template>
  <div
    id="sequence_settings"
    class="flex w-full justify-center mt-2 overflow-y-scroll custom_scrollbar"
    style="height: calc(100vh - (200px + var(--banner-height)))"
  >
    <div class="flex flex-col gap-10 w-1/2">
      <div class="flex flex-col gap-4">
        <span class="text_color font-semibold">General</span>
        <div>
          <span class="secondary_color text-[13px]">Who can access</span>
          <a-select v-model:value="form_state.permission" class="w-full mt-2">
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>
            <a-select-option
              v-for="(permission, index) in permissionOptions"
              :key="index"
              :value="permission.value"
              :label="permission.label"
            >
              {{ permission.label }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <span class="text_color font-semibold">Schedule</span>
        <div class="flex flex-col mt-2">
          <span class="secondary_color text-[13px]">Timezone</span>
          <a-select
            v-model:value="form_state.timezone"
            class="w-full mt-2"
            showSearch
          >
            <template #suffixIcon
              ><ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
            /></template>
            <a-select-option
              v-for="timezone in timeZones"
              :key="`timezone_${timezone}`"
              :value="timezone"
            >
              {{ timezone }}
            </a-select-option>
          </a-select>
          <div class="flex flex-row items-center mt-2">
            <a-checkbox v-model:checked="form_state.prefer_recipient_timezone" />
            <span class="text-xs text_color proxima_nova ml-2">
              Use the lead/contact's local time zone instead of the schedule's time zone, if the lead/contact contains location data.
            </span>
          </div>
        </div>

        <div class="flex flex-col mt-4">
          <span class="secondary_color text-[13px]">Active Days</span>
          <a-select
            v-model:value="form_state.active_days"
            class="w-full mt-2"
            mode="multiple"
            :options="activeDaysOptions"
          >
            <template #suffixIcon
              ><ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
            /></template>
          </a-select>
        </div>

        <div class="mt-4">
          <span class="secondary_color text-[13px]">Tags</span>
          <div class="flex flex-col gap-2">
            <a-select
              v-model:value="form_state.tags"
              mode="tags"
              class="w-full mt-2"
              :placeholder="`Add tags to organize your ${featureNames.SEQUENCES}`"
              :tokenSeparators="[',']"
              :options="tagOptions"
              :fieldNames="{ label: 'label', value: 'value' }"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>
            </a-select>
            <span class="text-xs secondary_color">
              Press Enter or comma to add new tags. Tags help organize and
              filter your {{ featureNames.SEQUENCES }}s.
            </span>
          </div>
        </div>

        <div class="flex justify-between gap-2">
          <div class="w-6/12 form_col">
            <span
              class="text-[13px] secondary_color proxima_nova font-normal mt-5"
              >Start Time</span
            >
            <a-select v-model:value="form_state.start_time" showSearch>
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="(time, index) in filteredStartTimeIntervals"
                :key="`${time}_time_${index}`"
                :value="time"
              >
                {{ time }}
              </a-select-option>
            </a-select>
          </div>
          <div class="w-6/12 form_col">
            <span
              class="text-[13px] secondary_color proxima_nova font-normal mt-5"
              >End Time</span
            >
            <a-select v-model:value="form_state.end_time" showSearch>
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="(time, index) in filteredEndTimeIntervals"
                :key="`${time}_time_${index}`"
                :value="time"
              >
                {{ time }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <br />
        <div class="flex flex-col gap-4">
          <span class="text_color font-semibold"
            >{{ featureNames.SEQUENCES }} Rules</span
          >

          <!-- Reply Rule -->
          <div class="flex flex-row items-center">
            <a-switch
              v-model:checked="form_state.finish_on_reply"
            />
            <span class="text-sm text_color proxima_nova ml-2">
              Mark contact as finished in {{ featureNames.SEQUENCES }} when
              there is a reply
            </span>
          </div>

          <!-- Phone Call Rule -->
          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.call_reply_exit_enabled" />
            <span class="text-sm text_color proxima_nova ml-2">
              Mark contact as finished in {{ featureNames.SEQUENCES }} when a
              phone call is connected
            </span>
          </div>

          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.remove_on_meeting_booked" />
            <span class="text-sm text_color proxima_nova ml-2">
              Mark contact as finished if a meeting is booked from related email
              step
            </span>
          </div>

          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.enrich_phone_by_default" />
            <span class="text-sm text_color proxima_nova ml-2">
              Enrich phone numbers when lead/contact is added to
              {{ featureNames.SEQUENCES }}
            </span>
          </div>

          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.continue_on_not_sent" />
            <span class="text-sm text_color proxima_nova ml-2">
              Continue to next step, if the current step is skipped or can't be sent
            </span>
          </div>

          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.exclude_added_entities" />
            <span class="text-sm text_color proxima_nova ml-2">
              Do not add contacts/leads if they are added to any other
              {{ featureNames.SEQUENCES }}
            </span>
          </div>

          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.exclude_people_from_lists" />
            <span class="text-sm text_color proxima_nova ml-2">
              Exclude people from lists
            </span>
          </div>

          <div v-if="form_state.exclude_people_from_lists" class="">
            <span class="secondary_color text-[13px]">List(s)</span>

            <ExcludeListsDropdown
              v-model="form_state.exclude_crm_lists"
              class="w-full mt-2"
              @create-list="openCreateListDrawer"
            />
          </div>
        </div>

        <div class="flex flex-col gap-4 mt-4">
          <span class="text_color font-semibold">Distribution</span>
          <!-- <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.enable_mailbox_rotation" />
            <span class="text-sm text_color proxima_nova font-semibold ml-2">
              Enable mailbox rotation for personal mailboxes
            </span>
          </div>
          <div class="flex flex-row items-center mt-2">
            <a-switch v-model:checked="form_state.round_robin_enabled" />
            <span class="text-sm text_color proxima_nova font-semibold ml-2">
              Assign contacts/leads to team members via round robin
            </span>
          </div> -->

          <!-- Team Selection (only visible when round robin is enabled) -->
          <div v-if="form_state.round_robin_enabled" class="mt-1">
            <span class="secondary_color text-[13px]"
              >Select Team for Round Robin</span
            >
            <a-select
              v-model:value="form_state.round_robin_team_id"
              class="w-full mt-2"
              placeholder="Select a team"
              :loading="teamsLoading"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="team in teams"
                :key="team.id"
                :value="team.id"
              >
                {{ team.name }}
              </a-select-option>
            </a-select>
          </div>

          <div class="flex flex-row items-center mt-2">
            <a-switch v-model:checked="form_state.call_round_robin_enabled" />
            <span class="text-sm text_color proxima_nova font-semibold ml-2">
              Assign phone call to team members via round robin
            </span>
          </div>

          <!-- Team Selection (only visible when round robin is enabled) -->
          <div v-if="form_state.call_round_robin_enabled" class="mt-1">
            <span class="secondary_color text-[13px]"
              >Select Team for call Round Robin</span
            >
            <a-select
              v-model:value="form_state.call_round_robin_team_id"
              class="w-full mt-2"
              placeholder="Select a team"
              :loading="teamsLoading"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="team in teams"
                :key="team.id"
                :value="team.id"
              >
                {{ team.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <div class="flex flex-col gap-4 mt-4">
          <div class="flex flex-row items-center">
            <a-switch v-model:checked="form_state.add_unsubscribe_link" />
            <div class="flex flex-col gap-0.5">
              <span class="text-sm text_color proxima_nova font-semibold ml-2">
                Add unsubscribe link to {{ featureNames.SEQUENCES }}
              </span>
              <span
                class="text-xs secondary_color proxima_nova font-normal ml-2"
              >
                Enable this option to include an unsubscribe link at the bottom
                of each email in this {{ featureNames.SEQUENCES }}
              </span>
            </div>
          </div>

          <div v-if="form_state.add_unsubscribe_link">
            <span class="text-sm secondary_color proxima_nova font-bold">
              Unsubscribe Message
            </span>
            <CustomInput
              format="text"
              v-model="form_state.unsubscribe_message"
              type="primary"
              class="my-1"
            />
            <span class="text-xs secondary_color proxima_nova font-bold">
              {{ unsubscribedLinkTitleTip }}
            </span>
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex flex-row items-center">
              <a-switch v-model:checked="form_state.catch_all_enabled" />
              <div class="flex flex-col gap-0.5">
                <span
                  class="text-sm text_color proxima_nova font-semibold ml-2"
                >
                  Exclude Catch-All Emails
                </span>
                <span
                  class="text-xs secondary_color proxima_nova font-normal ml-2"
                >
                  Block emails to domains that accept all addresses, valid or
                  invalid.
                </span>
              </div>
            </div>

            <div class="flex flex-row items-center mt-2">
              <a-switch v-model:checked="form_state.skip_email_validation" />
              <div class="flex flex-col gap-0.5">
                <span
                  class="text-sm text_color proxima_nova font-semibold ml-2"
                >
                  Skip Email Validation
                </span>
                <span
                  class="text-xs secondary_color proxima_nova font-normal ml-2"
                >
                  ⚠️ Warning: This bypasses email validation. Only enable if
                  you're confident in your email data quality.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add entity list component -->
  <AddEntityList
    source="sequence_settings"
    :drawerVisible="showCreateListDrawer"
    @update:modelValue="handleCreateListDrawerClose"
  />
</template>

<script setup>
import {
  permissionOptions,
  activeDaysOptions,
} from "@/src/constants/sequenceConstants.js";

import { timeZones } from "@/src/constants/timeZoneConstants.js";
import { onMounted, reactive, ref, watch, computed } from "vue";
import { generateTimeOptions, filterTimeOptions } from "@/src/utils/utils.js";
import CustomInput from "@/src/components/CustomInput.vue";
import SequenceService from "@/src/api/SequenceService.js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import DashboardService from "../../../api/DashboardService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import ExcludeListsDropdown from "@/src/components/ExcludeListsDropdown.vue";
import AddEntityList from "@/src/views/entity_lists/AddEntityList.vue";
import { featureNames } from "@/src/constants/globalConstants";

dayjs.extend(utc);

const props = defineProps({
  sequenceId: {
    type: [String, Number],
    required: false,
  },
  initialTags: {
    type: Array,
    default: () => [],
  },
});

const isVisible = ref(false);
const tagOptions = ref([]);
const timeIntervals = generateTimeOptions();
const settingsFetched = ref(false);
const teamsLoading = ref(false);
const teams = ref([]);
const showCreateListDrawer = ref(false);
const unsubscribedLinkTitleTip = ref(
  "Make sure the unsubscribe link is added in the following format {{ unsubscribe link title here }}",
);

const filteredStartTimeIntervals = computed(() => {
  return filterTimeOptions(timeIntervals, form_state.end_time, 'before');
});

const filteredEndTimeIntervals = computed(() => {
  return filterTimeOptions(timeIntervals, form_state.start_time, 'after');
});

let form_state = reactive({
  permission: "private",
  timezone: "America/Los_Angeles",
  prefer_recipient_timezone: false,
  active_days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
  start_time: "9:30am",
  end_time: "5:30pm",
  add_unsubscribe_link: false,
  catch_all_enabled: false,
  skip_email_validation: false,
  round_robin_enabled: false,
  finish_on_reply: true,
  call_reply_exit_enabled: false,
  remove_on_meeting_booked: false,
  exclude_added_entities: false,
  exclude_people_from_lists: false,
  exclude_crm_lists: [],
  enable_mailbox_rotation: false,
  round_robin_team_id: null,
  call_round_robin_team_id: null,
  call_round_robin_enabled: false,
  enrich_phone_by_default: false,
  continue_on_not_sent: false,
  tags: [],
  unsubscribe_message:
    "No longer interested in hearing from me? {{ unsubscribe }}",
});

watch(
  () => form_state.round_robin_enabled,
  (newValue) => {
    if (!newValue) {
      form_state.round_robin_team_id = null;
    }
  },
);

watch(
  () => form_state.call_round_robin_enabled,
  (newValue) => {
    if (!newValue) {
      form_state.call_round_robin_team_id = null;
    }
  },
);

const loadTags = async () => {
  try {
    const response = await SequenceService.getTagsForSequenceFilters();
    if (response?.success && response.tags) {
      tagOptions.value = response.tags.map((tag) => ({
        label: tag.name,
        value: tag.id,
      }));
    }
  } catch (error) {
    console.error("Error loading tags:", error);
  }
};

watch(
  () => props.visible,
  (newVal) => {
    isVisible.value = newVal;
    if (newVal) {
      form_state.tags = [...props.initialTags];
      loadTags();
    }
  },
  { immediate: true },
);

onMounted(() => {
  form_state.tags = [...props.initialTags];
  loadTags();
  fetchSequenceSettings();
  fetchTeams();
});

const fetchTeams = async () => {
  teamsLoading.value = true;
  try {
    const response = await DashboardService.fetchTeams();
    if (response.success) {
      teams.value = response.teams;
    }
  } catch (error) {
    console.error("Error fetching teams:", error);
  } finally {
    teamsLoading.value = false;
  }
};

const fetchSequenceSettings = () => {
  SequenceService.fetchSequenceSettings(props.sequenceId)
    .then((response) => {
      if (response.success) {
        const settings = response.settings;
        form_state.permission = settings.permission || form_state.permission;
        form_state.timezone = settings.timezone || form_state.timezone;
        form_state.prefer_recipient_timezone =
          settings.prefer_recipient_timezone || form_state.prefer_recipient_timezone;
        form_state.active_days = settings.active_days || form_state.active_days;
        form_state.round_robin_enabled =
          settings.round_robin_enabled || form_state.round_robin_enabled;
        form_state.call_reply_exit_enabled =
          settings.call_reply_exit_enabled ||
          form_state.call_reply_exit_enabled;
        form_state.remove_on_meeting_booked =
          settings.remove_on_meeting_booked ||
          form_state.remove_on_meeting_booked;
        form_state.exclude_added_entities =
          settings.exclude_added_entities || form_state.exclude_added_entities;
        form_state.exclude_people_from_lists =
          settings.exclude_people_from_lists ||
          form_state.exclude_people_from_lists;
        form_state.exclude_crm_lists =
          settings.exclude_crm_lists || form_state.exclude_crm_lists;
        form_state.enable_mailbox_rotation =
          settings.enable_mailbox_rotation ||
          form_state.enable_mailbox_rotation;
        form_state.round_robin_team_id =
          settings.round_robin_team_id || form_state.round_robin_team_id;
        form_state.call_round_robin_team_id =
          settings.call_round_robin_team_id ||
          form_state.call_round_robin_team_id;
        form_state.call_round_robin_enabled =
          settings.call_round_robin_enabled ||
          form_state.call_round_robin_enabled;
        form_state.enrich_phone_by_default =
          settings.enrich_phone_by_default ||
          form_state.enrich_phone_by_default;
        form_state.continue_on_not_sent =
          settings.continue_on_not_sent || form_state.continue_on_not_sent;
        form_state.finish_on_reply =
          settings.finish_on_reply ?? form_state.finish_on_reply;
        form_state.tags = settings.tags;
        form_state.start_time = settings.start_time
          ? dayjs(settings.start_time, "HH:mm").format("h:mma")
          : form_state.start_time;
        form_state.end_time = settings.end_time
          ? dayjs(settings.end_time, "HH:mm").format("h:mma")
          : form_state.end_time;
        form_state.add_unsubscribe_link =
          settings.add_unsubscribe_link || form_state.add_unsubscribe_link;
        form_state.unsubscribe_message =
          settings.unsubscribe_message || form_state.unsubscribe_message;
        form_state.catch_all_enabled =
          settings.catch_all_enabled || form_state.catch_all_enabled;
        form_state.skip_email_validation =
          settings.skip_email_validation || form_state.skip_email_validation;
      }
    })
    .catch((error) => {
      console.error("Failed to fetch settings:", error);
    })
    .finally(() => {
      settingsFetched.value = true;
    });
};

const saveSequenceSettings = () => {
  const formDataToSend = { ...form_state };
  if (formDataToSend.start_time) {
    formDataToSend.start_time = dayjs
      .utc(formDataToSend.start_time, "h:mma")
      .format("HH:mm");
  }
  if (formDataToSend.end_time) {
    formDataToSend.end_time = dayjs
      .utc(formDataToSend.end_time, "h:mma")
      .format("HH:mm");
  }

  formDataToSend.exclude_crm_lists = formDataToSend.exclude_crm_lists;

  SequenceService.saveSequenceSettings(props.sequenceId, formDataToSend)
    .then((response) => {
      if (response?.success) {
        console.log("Sequence settings saved successfully");
      }
    })
    .catch((error) => {
      console.error("Failed to save Sequence settings:", error);
    });
};

watch(
  () => form_state,
  (newValue) => {
    if (settingsFetched.value) {
      saveSequenceSettings();
    }
  },
  { deep: true },
);

const openCreateListDrawer = () => {
  showCreateListDrawer.value = true;
};

const handleCreateListDrawerClose = (visible) => {
  showCreateListDrawer.value = visible;
};
</script>
